import React from 'react';
import {
  ColorValues,
  Flex,
  GoodsCarousel,
  GoodsCarouselSkeleton,
  Headline,
  Typography,
} from '@banhala/ui-kit';
import type { CodiGoodsItem } from 'shared/src/api';
import { useLogDisplayGoods } from '../../../../hooks/useLogDisplayGoods';
import { bridge, useNavigator } from 'shared/src/utils';
import { useRemoveLikeGoods } from '../../queries/useRemoveLikeGoods';
import { useMidRelatedRecommendGoodsList } from './queries/useMidRelatedRecommendGoodsList';
import { useAddLikeGoods } from 'shared/src/hooks';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useLikeMap } from 'shared/src/hooks';

const MidRelatedRecommendAdGoodsCarouselContent: React.FC = () => {
  const { data: midRelatedGoodsList } = useMidRelatedRecommendGoodsList();

  const { logDisplayGoods } = useLogDisplayGoods();

  const { likeMap, setLikeMap } = useLikeMap(
    midRelatedGoodsList.item_list.reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: val.item.like.is_liked,
      };
    }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();

  if (midRelatedGoodsList.item_list.length === 0) return null;

  return (
    <Flex>
      <Headline
        headline={midRelatedGoodsList.title}
        size={'small'}
        topCompact={true}
        testID={midRelatedGoodsList.title}
        rightPanel={
          <Typography type={'body2'} rawColor={ColorValues.gray30}>
            sponsored
          </Typography>
        }
      />
      <GoodsCarousel<CodiGoodsItem>
        list={midRelatedGoodsList.item_list}
        listTestID={`LIST_ID=${midRelatedGoodsList.item_list[0]?.logging.analytics.LIST_ID}`}
        onPressItem={async (pressed, index) => {
          const { item, logging } = pressed;
          bridge.logAnalyticsEvent({
            name: 'CLICK_GOODS',
            analytics: {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            },
          });
          navigator.moveToGoods({
            sno: item.sno,
            firstPageRendering: item.first_page_rendering,
          });
        }}
        onPressLike={async (pressed, index) => {
          const { item, logging } = pressed;

          const analytics: LogAnalyticsEventParams['analytics'] = {
            SCREEN: 'GOODS_DETAIL',
            ...logging.analytics,
            INDEX: index,
          };

          if (!likeMap[item.sno]) {
            bridge.logAnalyticsEvent({
              name: 'CLICK_LIKE_GOODS',
              analytics,
            });
            // 좋아요 누르기
            addLikeGoods(item.sno, {
              SCREEN: 'GOODS_DETAIL',
            });
            return;
          }

          bridge.logAnalyticsEvent({
            name: 'CLICK_UNLIKE_GOODS',
            analytics,
          });

          await mutateRemoveLikeGoods({
            sno: item.sno,
          });

          //해제하기
          bridge.likeGoods({
            goods_sno: item.sno,
            is_liked: false,
          });
          setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
        }}
        size={'medium'}
        onViewableItemsChanged={logDisplayGoods}
      ></GoodsCarousel>
    </Flex>
  );
};

export const MidRelatedRecommendAdGoodsCarousel: React.FC = () => {
  return (
    <React.Suspense
      fallback={
        <Flex>
          <Flex.Row
            padding={{ horizontal: 16, vertical: 16 }}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Flex
              backgroundColor={'bg_tertiary'}
              width={98}
              height={24}
              borderRadius={4}
            ></Flex>
            <Flex
              backgroundColor={'bg_tertiary'}
              width={64}
              height={18}
              borderRadius={4}
            ></Flex>
          </Flex.Row>
          <GoodsCarouselSkeleton count={4} />
        </Flex>
      }
    >
      <MidRelatedRecommendAdGoodsCarouselContent />
    </React.Suspense>
  );
};
